/**
 * La base de personas: las de una campaña (`campanaId`) o las de todas las campañas del comercio juntas.
 *
 * - Los filtros, la búsqueda, el orden y la página viven en la URL (`?filtro=&q=&orden=&pagina=`): un enlace o un F5
 *   muestran lo mismo. Cada filtro dice cuántas personas tiene.
 * - El mail se ve entero (es la persona en una base de mails); el teléfono, enmascarado como en WhatsApp. El CSV
 *   trae todo: su nombre lo dice («PII») y el servidor anota quién lo bajó. El nombre abre la ficha de la persona.
 * - Aperturas y clics que no se miden (apagados en Snov) van como «—», nunca como 0.
 * - Una respuesta vieja (se tipeó rápido) no pisa a la nueva. Un 404 (la campaña dejó de ser de este comercio) borra
 *   lo que se veía: no se siguen mostrando sus personas.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Loader2, Search, Users } from "lucide-react";
import { emailsApi } from "../api";
import type { OrdenPersona, PaginaPersonas } from "../types";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { destinoParaMostrar } from "../../wa-oficial/logic/formato";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { num, plural } from "../../campana-nueva/utils/format";
import { FILTROS_PERSONA, R_EMAILS, descargarTexto, diaMesHora, esFiltroPersona } from "../logic/emails";
import { ChipEstadoPersona } from "./Chips";
import { useEstadoEmails } from "../contexto";

export const POR_PAGINA = 50;
const ORDENES: { valor: OrdenPersona; texto: string }[] = [
  { valor: "reciente", texto: "Actividad más reciente" },
  { valor: "interaccion", texto: "Más interacción" },
  { valor: "nombre", texto: "Nombre" },
];
const esOrden = (v: string | null): v is OrdenPersona => ORDENES.some((o) => o.valor === v);

/** Un número que puede no medirse: «—» con el porqué (para el lector de pantalla, también). */
function Medido({ n }: { n: number | null }) {
  if (n === null) return <span className="text-slate-300" title="No se mide en sus campañas (apagado en Snov)">—<span className="sr-only"> no se mide</span></span>;
  return <>{num(n)}</>;
}

export function TablaPersonas({ campanaId }: { campanaId?: number }) {
  const [sp, setSp] = useSearchParams();
  const { version } = useEstadoEmails();
  const filtroUrl = sp.get("filtro");
  const filtro = esFiltroPersona(filtroUrl) ? filtroUrl : "todas";
  const ordenUrl = sp.get("orden");
  const orden = esOrden(ordenUrl) ? ordenUrl : "reciente";
  const q = sp.get("q") || "";
  const pagina = Math.max(1, Number.parseInt(sp.get("pagina") || "1", 10) || 1);

  const [texto, setTexto] = useState(q);
  const [datos, setDatos] = useState<PaginaPersonas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [reintento, setReintento] = useState(0);
  const [bajando, setBajando] = useState(false);
  const [avisoCsv, setAvisoCsv] = useState("");
  const seq = useRef(0);
  const idBuscar = useId();
  const idOrden = useId();

  const cambiar = useCallback((k: string, v: string) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (v) n.set(k, v); else n.delete(k);
      if (k !== "pagina") n.delete("pagina");
      return n;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => { setTexto(q); }, [q]);
  useEffect(() => {
    if (texto.trim() === q) return;
    const t = setTimeout(() => cambiar("q", texto.trim().slice(0, 80)), 400);
    return () => clearTimeout(t);
  }, [texto, q, cambiar]);

  useEffect(() => {
    const mio = ++seq.current;
    setCargando(true);
    void (async () => {
      let nuevo: PaginaPersonas | null = null;
      let err = "";
      let cod = "";
      try {
        const l = leerRespuesta<PaginaPersonas>(await emailsApi.personas({ campana: campanaId, filtro, q: q || undefined, orden, pagina, porPagina: POR_PAGINA }));
        if (!l.ok || !l.data) err = l.ok ? "El servidor no devolvió las personas." : textoError(l);
        else nuevo = l.data;
      } catch (e) {
        const x = leerError(e);
        err = textoError(x);
        cod = x.code;
      }
      if (mio !== seq.current) return;
      setCargando(false);
      setError(err);
      if (nuevo) setDatos(nuevo);
      else if (cod === "NOT_FOUND") setDatos(null);
    })();
  }, [campanaId, filtro, q, orden, pagina, reintento, version]);

  const descargar = async () => {
    if (bajando) return;
    setBajando(true);
    setAvisoCsv("");
    try {
      const l = leerRespuesta<{ nombre: string; contenido: string; filas: number; recortado: boolean }>(
        await emailsApi.exportarPersonas({ campana: campanaId, filtro, q: q || undefined, orden }),
      );
      if (!l.ok || !l.data) { setAvisoCsv(l.ok ? "No pudimos armar el archivo." : textoError(l)); return; }
      descargarTexto(l.data.nombre, l.data.contenido);
      if (l.data.recortado) setAvisoCsv(`El archivo trae las primeras ${num(l.data.filas)} personas: filtrá para bajar el resto.`);
    } catch (e) {
      setAvisoCsv(textoError(leerError(e)));
    } finally {
      setBajando(false);
    }
  };

  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const deComercio = campanaId === undefined;
  // En una campaña sin seguimiento la columna no va; en la base del comercio va, y cada fila dice si se le mide.
  const sinAperturas = datos?.campana?.seguimiento.aperturas === false;
  const sinClics = datos?.campana?.seguimiento.clics === false;
  const filtros = FILTROS_PERSONA.filter(
    (f) => !(sinAperturas && f.valor === "abrieron") && !(sinClics && f.valor === "clic") && !(f.soloSiHay && filtro !== f.valor && !datos?.porFiltro?.[f.valor]),
  );
  const columnas = 5 + Number(!sinAperturas) + Number(!sinClics) + Number(deComercio);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filtrar personas">
        {filtros.map((f) => {
          const n = datos?.porFiltro?.[f.valor];
          const activo = filtro === f.valor;
          return (
            <button key={f.valor} type="button" aria-pressed={activo} title={f.titulo} onClick={() => cambiar("filtro", f.valor === "todas" ? "" : f.valor)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border ${activo ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {f.texto}{n !== undefined ? <span className={`ml-1.5 tabular-nums ${activo ? "text-violet-100" : "text-slate-400"}`}>{num(n)}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <label htmlFor={idBuscar} className="sr-only">Buscar personas</label>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input id={idBuscar} value={texto} maxLength={80} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar por nombre, mail o teléfono"
            className="w-full h-10 rounded-xl border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        <div>
          <label htmlFor={idOrden} className="block text-[11px] font-semibold text-slate-500">Ordenar por</label>
          <select id={idOrden} value={orden} onChange={(e) => cambiar("orden", e.target.value === "reciente" ? "" : e.target.value)}
            className="mt-1 h-10 rounded-xl border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            {ORDENES.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
          </select>
        </div>
        <button type="button" onClick={() => void descargar()} disabled={bajando || !total}
          className="h-10 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          {bajando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Descargar CSV
        </button>
      </div>
      {avisoCsv && <p className="text-xs text-amber-700" role="status">{avisoCsv}</p>}

      {cargando && !datos ? (
        <Esqueleto alto="h-64" etiqueta="Cargando personas" />
      ) : error && !datos ? (
        <ErrorTarjeta texto={`No pudimos traer las personas. ${error}`} onReintentar={() => setReintento((n) => n + 1)} />
      ) : !datos || !datos.total ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <Users size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600">
            {filtro !== "todas" || q ? "Nadie coincide con el filtro o la búsqueda." : deComercio ? "Todavía no se le mandó ningún mail a nadie de este comercio." : "Esta campaña todavía no le mandó a nadie."}
          </p>
          {(filtro !== "todas" || q) && (
            <button type="button" className="mt-3 text-sm font-semibold text-violet-700 underline"
              onClick={() => {
                setTexto("");
                // sólo lo de la tabla: en el detalle de una campaña la URL también dice en qué pestaña se está
                setSp((prev) => {
                  const n = new URLSearchParams(prev);
                  for (const k of ["filtro", "q", "pagina"]) n.delete(k);
                  return n;
                }, { replace: true });
              }}>
              Ver todas
            </button>
          )}
        </div>
      ) : (
        <div className={`space-y-3 ${cargando ? "opacity-60" : ""}`} aria-busy={cargando || undefined}>
          {error && <p className="text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
          <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Persona</th>
                  <th className="px-4 py-2.5">Cómo le fue</th>
                  <th className="px-4 py-2.5 text-right">Mails</th>
                  {!sinAperturas && <th className="px-4 py-2.5 text-right">Aperturas</th>}
                  {!sinClics && <th className="px-4 py-2.5 text-right">Clics</th>}
                  <th className="px-4 py-2.5 text-right">Respuestas</th>
                  <th className="px-4 py-2.5">Última actividad</th>
                  {deComercio && <th className="px-4 py-2.5">Campañas</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!datos.filas.length && (
                  <tr>
                    <td colSpan={columnas} className="px-4 py-6 text-center text-sm text-slate-500">
                      Esta página ya no tiene personas.{" "}
                      <button type="button" onClick={() => cambiar("pagina", "")} className="font-semibold text-violet-700 underline">Ir a la primera</button>
                    </td>
                  </tr>
                )}
                {datos.filas.map((p, i) => (
                  <tr key={p.id || `${i}-${p.email}`} className="align-top">
                    <td className="px-4 py-3 min-w-[220px]">
                      {p.id ? (
                        <Link to={`${R_EMAILS}/personas/${encodeURIComponent(p.id)}`} className="font-semibold text-slate-900 hover:text-violet-700 hover:underline">{p.nombre || "Sin nombre"}</Link>
                      ) : (
                        <p className="font-semibold text-slate-900">{p.nombre || "Sin nombre"}</p>
                      )}
                      <p className="text-xs text-slate-500 break-all">{p.email}</p>
                      {p.telefono && <p className="text-[11px] text-slate-400 font-mono">{destinoParaMostrar(p.telefono, false)}</p>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ChipEstadoPersona estado={p.estado} />
                      {p.enNoEnviar && (
                        <span className="mt-1 block w-fit text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-800 text-white"
                          title="Está en la lista de «No enviar» de Snov: ninguna campaña le escribe">
                          En «No enviar»
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{num(p.enviados)}</td>
                    {!sinAperturas && <td className="px-4 py-3 text-right tabular-nums"><Medido n={p.aperturas} /></td>}
                    {!sinClics && <td className="px-4 py-3 text-right tabular-nums"><Medido n={p.clics} /></td>}
                    <td className="px-4 py-3 text-right tabular-nums">{num(p.respuestas)}{p.autorespuestas ? <span className="block text-[11px] text-slate-400">{num(p.autorespuestas)} {plural(p.autorespuestas, "automática", "automáticas")}</span> : null}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{diaMesHora(p.ultimoEvento)}</td>
                    {deComercio && (
                      <td className="px-4 py-3 min-w-[160px]">
                        {(p.campanas || []).map((c) => (
                          <Link key={c.id} to={`${R_EMAILS}/campanas/${encodeURIComponent(String(c.id))}`} className="block text-xs text-violet-700 hover:underline">{c.titulo || `Campaña ${c.id}`}</Link>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav aria-label="Páginas" className="flex items-center justify-between gap-3 text-sm">
            <button type="button" onClick={() => cambiar("pagina", pagina - 1 > 1 ? String(pagina - 1) : "")} disabled={pagina <= 1}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Anterior
            </button>
            <span className="text-slate-600 text-center">Página {num(pagina)} de {num(paginas)} · {num(total)} {plural(total, "persona", "personas")}</span>
            <button type="button" onClick={() => cambiar("pagina", String(pagina + 1))} disabled={pagina >= paginas}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Siguiente
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}

export default TablaPersonas;
